import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnDestroy } from '@angular/core';

/** Shortest time the loader stays up once shown, so a fast operation reads as loading, not a flicker. */
const MIN_VISIBLE_MS = 450;

/**
 * Wraps a section and covers it with the app's loading animation (the same overlay the GPS
 * module uses: translucent veil, loading gif and a caption) while `active`.
 *
 * The content stays rendered underneath, so form values, maps, ViewChild refs and scroll
 * position survive, and a failed save leaves the user's input intact. Interaction with the
 * content is blocked while the loader is up.
 *
 * Usage:
 * <hmt-loading-overlay [active]="saving$ | async" message="Saving...">
 *   <form>...</form>
 * </hmt-loading-overlay>
 */
@Component({
  selector: 'hmt-loading-overlay',
  templateUrl: './loading-overlay.component.html',
  styleUrls: ['./loading-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LoadingOverlayComponent implements OnDestroy {
  @Input() message = 'Loading...';

  @HostBinding('class.hmt-loading-active')
  visible = false;

  @HostBinding('attr.aria-busy')
  get ariaBusy(): string | null {
    return this.visible ? 'true' : null;
  }

  private shownAt = 0;
  private hideTimer: ReturnType<typeof setTimeout>;

  constructor(private changeDetectorRef: ChangeDetectorRef) {
  }

  @Input() set active(value: boolean | null) {
    clearTimeout(this.hideTimer);
    if (value) {
      if (!this.visible) {
        this.visible = true;
        this.shownAt = Date.now();
      }
      return;
    }
    if (!this.visible) {
      return;
    }
    const remaining = MIN_VISIBLE_MS - (Date.now() - this.shownAt);
    if (remaining <= 0) {
      this.visible = false;
      return;
    }
    this.hideTimer = setTimeout(() => {
      this.visible = false;
      this.changeDetectorRef.markForCheck();
    }, remaining);
  }

  ngOnDestroy(): void {
    clearTimeout(this.hideTimer);
  }
}
